// MF-566 — Card 'welfare-incidents': the "Welfare Incidents" block of the Belmont dashboard, in levels (MF-574):
// 1) positive / negative / other; a click on a group shows 2) the types inside it (bars coloured by what they
// mean: blue good / orange attention / grey other); a click on a type shows 3) the incidents behind it. The ‹ goes
// back one level. Rows: { type,
// category, tone, incident_date, n }. For one student a row is one incident; for the school it is a count
// of a type + category. Only dates and categories are shown, never the free text of a record.
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, ButtonBase, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons'
import { SUMMARY_PALETTE, TONE_COLORS, FONT, INK, baseTextStyle, tooltipBase, fmtValue } from '../boardTheme'

const TOP = 12

/** rows → [{ type, n, tone, items[] }] biggest first; a type's colour is the tone with most incidents. */
export const groupTypes = (rows = []) => {
  const by = new Map()
  for (const r of rows) {
    if (!by.has(r.type)) by.set(r.type, { type: r.type, n: 0, tones: new Map(), items: [] })
    const g = by.get(r.type)
    const n = Number(r.n) || 0
    g.n += n
    g.tones.set(r.tone, (g.tones.get(r.tone) || 0) + n)
    g.items.push(r)
  }
  return [...by.values()]
    .map((g) => ({ ...g, tone: [...g.tones.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || 'neutral' }))
    .sort((a, b) => b.n - a.n || a.type.localeCompare(b.type))
    .slice(0, TOP)
}

const TONE_LABELS = { positive: 'Positive', negative: 'Negative', neutral: 'Other' }
const toneOf = (r) => (r.tone === 'positive' || r.tone === 'negative' ? r.tone : 'neutral')

/** rows → the three groups (only those with incidents), positive first. */
export const groupTones = (rows = []) =>
  ['positive', 'negative', 'neutral']
    .map((tone) => ({
      type: TONE_LABELS[tone],
      tone,
      n: rows.filter((r) => toneOf(r) === tone).reduce((sum, r) => sum + (Number(r.n) || 0), 0)
    }))
    .filter((g) => g.n > 0)

const dmy = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('-') : null)

const WelfareIncidentsCard = ({ data }) => {
  const opts = data?.meta?.card?.options || {}
  const rows = useMemo(() => data?.rows || [], [data])
  const [tone, setTone] = useState(null)
  const [picked, setPicked] = useState(null)
  // a new student / new data: go back to the first level
  useEffect(() => {
    setTone(null)
    setPicked(null)
  }, [data])
  // what the chart draws: the groups, or the types inside the chosen group
  const types = useMemo(
    () => (tone ? groupTypes(rows.filter((r) => toneOf(r) === tone)) : groupTones(rows)),
    [rows, tone]
  )
  const current = tone ? types.find((t) => t.type === picked) : null

  const option = useMemo(
    () => ({
      textStyle: baseTextStyle,
      animationDuration: 400,
      tooltip: { ...tooltipBase, trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { top: 8, left: 8, right: 36, bottom: 8, containLabel: true },
      xAxis: {
        type: 'value',
        minInterval: 1,
        splitLine: { lineStyle: { color: INK.grid } },
        axisLabel: { color: INK.muted, fontSize: 11 }
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: types.map((t) => t.type),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: INK.axis } },
        axisLabel: { color: INK.secondary, fontSize: 11, width: 140, overflow: 'truncate', fontFamily: FONT }
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 28,
          cursor: 'pointer',
          data: types.map((t) => ({
            value: t.n,
            itemStyle: { color: TONE_COLORS[t.tone] || TONE_COLORS.neutral, borderRadius: [0, 4, 4, 0] }
          })),
          label: {
            show: true,
            position: 'right',
            fontSize: 11,
            color: INK.secondary,
            formatter: ({ value }) => fmtValue(value, 0)
          }
        }
      ]
    }),
    [types]
  )

  const ref = useRef(null)
  const box = useRef(null)
  useEffect(() => {
    const el = box.current
    if (!el) return undefined
    const ro = new ResizeObserver(() => ref.current?.getEchartsInstance?.()?.resize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [current])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {current ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ButtonBase
              aria-label='Back to all types of incident'
              onClick={() => setPicked(null)}
              sx={{ width: 32, height: 32, borderRadius: '8px', color: SUMMARY_PALETTE.muted }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </ButtonBase>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
              {current.type} · {current.n} {current.n === 1 ? 'incident' : 'incidents'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {current.items.map((it, i) => (
              <Box
                key={`${it.incident_date}-${it.category}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${SUMMARY_PALETTE.grid}`
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: TONE_COLORS[it.tone] || TONE_COLORS.neutral
                  }}
                />
                <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, minWidth: 84 }}>
                  {dmy(it.incident_date) || ''}
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: SUMMARY_PALETTE.ink, flex: 1, overflowWrap: 'anywhere' }}
                >
                  {it.category}
                </Typography>
                {Number(it.n) > 1 ? (
                  <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.good }}>
                    {fmtValue(it.n, 0)}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <>
          {tone ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ButtonBase
                aria-label='Back to positive and negative'
                onClick={() => setTone(null)}
                sx={{ width: 32, height: 32, borderRadius: '8px', color: SUMMARY_PALETTE.muted }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </ButtonBase>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
                {TONE_LABELS[tone]} · {types.reduce((sum, t) => sum + t.n, 0)} incidents
              </Typography>
            </Box>
          ) : opts.hint ? (
            <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mb: 0.5 }}>{opts.hint}</Typography>
          ) : null}
          {opts.heading && !tone ? (
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: SUMMARY_PALETTE.ink, textAlign: 'center', mb: 0.5 }}
            >
              {opts.heading}
            </Typography>
          ) : null}
          <div ref={box} style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
            <ReactECharts
              ref={ref}
              option={option}
              notMerge
              style={{ height: '100%', width: '100%' }}
              onEvents={{
                click: (e) => {
                  const hit = types[e.dataIndex]
                  if (!hit) return
                  if (tone) setPicked(hit.type)
                  else setTone(hit.tone)
                }
              }}
            />
          </div>
        </>
      )}
    </Box>
  )
}

export default WelfareIncidentsCard
